import { useState } from 'react';
import { withQuery } from '@/api/client';
import { useFormFactor } from '@/lib/formFactor';
import { useSession } from '@/stores/session';
import { Icon } from '@/components/Icon';
import type { Page } from '@/api/types';
import { Empty, ErrorState, Skeleton } from '@/components/States';
import { date, dateTime, shortDate } from '@/lib/format';
import { Chips, DataTable, DeskHead, MoreHint, Pager, PhoneBar, SearchBox, type Col } from './kit/Kit';
import { useApi, useAutoMore, usePaged, useUrlState } from './kit/hooks';
import { actionLabel, entityModule } from './status';
import { AuditCleanupDialog } from './AuditCleanupDialog';
import type { AuditLog } from './types';
import s from './AuditLogsPage.module.css';

const WEEK = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
const MODULES = [{ key: 'orders', label: '订单' }, { key: 'inventory', label: '库存' }, { key: 'purchase', label: '采购' }, { key: 'settlement', label: '结算' }, { key: 'data', label: '资料' }, { key: 'system', label: '系统' }];

function dayLabel(v: string, now = new Date()) {
  const d = new Date(v);
  const k = date(v);
  if (k === date(now.toISOString())) return `今天 ${shortDate(v)}`;
  const y = new Date(now);
  y.setDate(y.getDate() - 1);
  if (k === date(y.toISOString())) return `昨天 ${shortDate(v)}`;
  return `${d.getFullYear() === now.getFullYear() ? shortDate(v) : k} ${WEEK[d.getDay()]}`;
}

function hm(v: string) {
  return dateTime(v).slice(6);
}

const who = (l: AuditLog) => l.user?.name || l.user?.username || '系统';

/**
 * 操作日志（system-*.html Y5）。
 * - 手机：按天分组的时间线 + 对象类型 chips；
 * - iPad / 桌面：表格 + 翻页。
 * V2 查询按模块、操作人及时间范围过滤；旧日志没有来源 IP 时显示空值。
 */
export function AuditLogsPage() {
  const ff = useFormFactor();
  const can = useSession((st) => st.can);
  const [sp, set] = useUrlState();
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [status, setStatus] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [draftActor, setDraftActor] = useState('');
  const [draftDays, setDraftDays] = useState('90');
  const q = sp.get('q') || '';
  const module = sp.get('module') || 'all';
  const actor = sp.get('actor') || '';
  const days = sp.get('days') || '30';
  const phone = ff === 'phone';
  const query = { keyword: q || undefined, module: module === 'all' ? undefined : module, actor: actor || undefined, days };
  const list = usePaged<AuditLog>(can('audit.read') ? '/api/v2/audit-logs' : null, query, phone ? 30 : 8, phone);
  const actors = useApi<Array<{ id: string; name: string; username: string }>>(can('audit.read') ? '/api/v2/audit-logs/actors' : null);
  const countPath = can('audit.read') ? '/api/v2/audit-logs' : null;
  const count90 = useApi<Page<AuditLog>>(countPath, { days: '90', pageSize: 1 });
  const countOrders = useApi<Page<AuditLog>>(countPath, { days: '90', module: 'orders', pageSize: 1 });
  const countInventory = useApi<Page<AuditLog>>(countPath, { days: '90', module: 'inventory', pageSize: 1 });
  const countPurchase = useApi<Page<AuditLog>>(countPath, { days: '90', module: 'purchase', pageSize: 1 });
  const countSettlement = useApi<Page<AuditLog>>(countPath, { days: '90', module: 'settlement', pageSize: 1 });
  const countData = useApi<Page<AuditLog>>(countPath, { days: '90', module: 'data', pageSize: 1 });
  const countSystem = useApi<Page<AuditLog>>(countPath, { days: '90', module: 'system', pageSize: 1 });
  const sentinel = useAutoMore(phone, list.loadMore);

  if (!can('audit.read')) return <Empty icon="lock" title="没有查看操作日志的权限">需要「查看操作日志」权限，请联系管理员。</Empty>;

  const moduleCounts: Record<string, number | undefined> = {
    all: count90.data?.pagination.total,
    orders: countOrders.data?.pagination.total,
    inventory: countInventory.data?.pagination.total,
    purchase: countPurchase.data?.pagination.total,
    settlement: countSettlement.data?.pagination.total,
    data: countData.data?.pagination.total,
    system: countSystem.data?.pagination.total,
  };
  const chips = <Chips value={module} onChange={(k) => set({ module: k === 'all' ? undefined : k })} items={[{ key: 'all', label: '全部', count: moduleCounts.all }, ...MODULES.map((item) => ({ ...item, count: moduleCounts[item.key] }))]} />;
  const search = <SearchBox value={q} onSearch={(v) => set({ q: v || undefined })} placeholder="单号 / 操作人 / 来源 IP / 内容" className={s.search} />;
  const clean = can('audit.delete') ? <button type="button" className={s.clean} onClick={() => setCleanupOpen(true)}>{phone ? '清理' : '清理 90 天前'}</button> : undefined;
  const exportUrl = withQuery('/api/v2/audit-logs/export', query);
  const exportAction = <a className={s.export} href={exportUrl} download="audit-logs.csv">导出</a>;
  const phoneExportAction = <a className={s.phoneIconAction} href={exportUrl} download="audit-logs.csv" aria-label="导出操作日志"><Icon name="down" size={20} /></a>;
  const filters = <div className={s.filters}><label>操作人<select aria-label="操作人" value={actor} onChange={(event) => set({ actor: event.target.value || undefined })}><option value="">全部</option>{actors.data?.map((item) => <option key={item.id} value={item.id}>{item.name} ({item.username})</option>)}</select></label><label>时间范围<select aria-label="时间范围" value={days} onChange={(event) => set({ days: event.target.value === '30' ? undefined : event.target.value })}><option value="7">近 7 天</option><option value="30">近 30 天</option><option value="90">近 90 天</option><option value="all">全部时间</option></select></label></div>;
  const filtered = q || module !== 'all' || actor || days !== '30';
  const activeFilterCount = Number(days !== '90') + Number(!!actor);
  const phoneModules = [{ key: 'all', label: '全部' }, ...MODULES];
  const applyPhoneFilters = () => {
    set({ actor: draftActor || undefined, days: draftDays === '90' ? undefined : draftDays });
    setFilterOpen(false);
  };

  const body = (() => {
    if (list.error && !list.items.length) return <ErrorState title="操作日志加载失败" message={list.error.message} detail="GET /api/v2/audit-logs" onRetry={list.reload} />;
    if (list.loading && !list.items.length) return <div className={s.pad}><Skeleton /><Skeleton /><Skeleton lines={3} /></div>;
    if (!list.items.length) return <Empty icon="hist" title={filtered ? '没有符合条件的日志' : '还没有操作日志'}>{filtered ? '换个关键词，或者切回「全部」再看看。' : '订单、库存、采购等写操作都会记在这里。'}</Empty>;
    return null;
  })();

  /* ------------------------------ 手机：按天分组的时间线 ------------------------------ */
  if (phone) {
    const dayGroups: { key: string; label: string; items: AuditLog[] }[] = [];
    for (const l of list.items) {
      const k = date(l.createdAt);
      let d = dayGroups[dayGroups.length - 1];
      if (!d || d.key !== k) {
        d = { key: k, label: dayLabel(l.createdAt), items: [] };
        dayGroups.push(d);
      }
      d.items.push(l);
    }
    return (
      <div className={s.phone}>
        <PhoneBar back="/settings" title="操作日志" sub={`近 90 天 ${(count90.data?.pagination.total ?? list.total).toLocaleString('zh-CN')} 条`} right={<div className={s.phoneHeadActions}>{phoneExportAction}<button type="button" aria-label="更多操作" aria-expanded={moreOpen} onClick={() => setMoreOpen((open) => !open)}><Icon name="more" size={20} /></button>{moreOpen && <div className={s.phoneMoreMenu} role="menu">{clean && <button type="button" role="menuitem" onClick={() => { setMoreOpen(false); setCleanupOpen(true); }}>清理 90 天前的日志</button>}</div>}</div>} />
        <div className={s.psearch}>{search}</div>
        <div className={s.phoneModuleRow}>
          <div className={s.phoneModules} role="tablist" aria-label="日志模块">
            {phoneModules.map((item) => <button type="button" role="tab" aria-selected={module === item.key} key={item.key} className={module === item.key ? s.phoneModuleOn : ''} onClick={() => set({ module: item.key === 'all' ? undefined : item.key, page: undefined })}><span>{item.label}</span><small>{moduleCounts[item.key]?.toLocaleString('zh-CN') ?? '—'}</small></button>)}
          </div>
          <button type="button" className={s.phoneFilterButton} aria-expanded={filterOpen} onClick={() => { setDraftActor(actor); setDraftDays(days); setFilterOpen(true); }}><Icon name="filter" size={16} />筛选{activeFilterCount > 0 && <b>{activeFilterCount}</b>}</button>
        </div>
        <div className={s.phoneActiveFilters}>
          <button type="button" className={s.phoneDateChip} onClick={() => set({ days: '90' })}>{days === 'all' ? '全部时间' : `近 ${days} 天`}<span aria-hidden="true">×</span><span className={s.srOnly}>清除时间范围</span></button>
          <button type="button" className={s.phoneActorChip} onClick={() => { setDraftActor(actor); setDraftDays(days); setFilterOpen(true); }}>操作人：{actor ? actors.data?.find((item) => item.id === actor)?.name || '已选' : '全部'}</button>
        </div>
        <div className={s.pbody}>
          {status && <p role="status" className={s.result}>{status}</p>}
          {body ||
            dayGroups.map((d) => (
              <section key={d.key} className={s.day}>
                <h3>{d.label}<small>{d.items.length} 条</small></h3>
                <ol className={s.tl}>
                  {d.items.map((l) => (
                    <li key={l.id}>
                      <div className={s.tlh}><b>{who(l)} · {actionLabel(l.action)}</b><time className="tnum">{hm(l.createdAt)}</time></div>
                      {l.summary && <p>{l.summary}</p>}
                      <span className={s.mod}>{entityModule(l.entityType)} · 来源 IP {l.sourceIp || '—'}</span>
                    </li>
                  ))}
                </ol>
              </section>
            ))}
          <MoreHint hasNext={list.hasNext} loading={list.loading} count={list.items.length} sentinel={sentinel} />
          <p className={s.note}>V2 权限编辑会记录逐项变化与原因；老版直接修改角色仍缺少逐项记录。</p>
        </div>
        {filterOpen && <div className={s.filterBackdrop} onClick={() => setFilterOpen(false)}>
          <section className={s.filterSheet} role="dialog" aria-modal="true" aria-labelledby="phone-filter-title" onClick={(event) => event.stopPropagation()}>
            <div className={s.filterSheetHead}><h2 id="phone-filter-title">筛选操作日志</h2><button type="button" aria-label="关闭筛选" onClick={() => setFilterOpen(false)}><Icon name="x" size={20} /></button></div>
            <label>操作人<select value={draftActor} onChange={(event) => setDraftActor(event.target.value)}><option value="">全部</option>{actors.data?.map((item) => <option key={item.id} value={item.id}>{item.name} ({item.username})</option>)}</select></label>
            <label>时间范围<select value={draftDays} onChange={(event) => setDraftDays(event.target.value)}><option value="7">近 7 天</option><option value="30">近 30 天</option><option value="90">近 90 天</option><option value="all">全部时间</option></select></label>
            <div className={s.filterSheetActions}><button type="button" onClick={() => { setDraftActor(''); setDraftDays('90'); }}>重置</button><button type="button" className={s.filterApply} onClick={applyPhoneFilters}>完成</button></div>
          </section>
        </div>}
        {cleanupOpen && <AuditCleanupDialog retainedCount={count90.data?.pagination.total ?? list.total} onClose={() => setCleanupOpen(false)} onDeleted={(count) => { setCleanupOpen(false); setStatus(`已清理 ${count} 条 90 天前日志`); list.reload(); }} />}
      </div>
    );
  }

  /* ------------------------------ iPad / 桌面：表格 ------------------------------ */
  const widths = ff === 'tablet' ? [114, 84, 68, 102, 300, 128] : [156, 118, 80, 135, 480, 145];
  const cols: Col<AuditLog>[] = [
    { key: 't', title: '时间', w: widths[0], render: (l) => <span className="tnum">{dateTime(l.createdAt)}</span> },
    { key: 'u', title: '操作人', w: widths[1], render: (l) => who(l) },
    { key: 'm', title: '模块', w: widths[2], render: (l) => <span className={s.modT} data-module={entityModule(l.entityType)}>{entityModule(l.entityType)}</span> },
    { key: 'a', title: '动作', w: widths[3], render: (l) => <b className={s.act}>{actionLabel(l.action)}</b> },
    { key: 'c', title: '对象与内容', w: widths[4], render: (l) => (l.summary ? <span title={l.summary}>{l.summary}</span> : <span className="em">—</span>) },
    { key: 'ip', title: '来源 IP', w: widths[5], render: (l) => <span className="tnum">{l.sourceIp || '—'}</span> },
  ];
  return (
    <div className={`${s.desk} ${ff === 'tablet' ? s.tablet : ''}`}>
      <DeskHead title="操作日志" sub={`近 90 天 ${moduleCounts.all?.toLocaleString('zh-CN') ?? '…'} 条 · 保留 90 天`} className={s.resourceWideHead}>
        {ff === 'tablet' && search}
        {exportAction}
        {clean}
      </DeskHead>
      <div className={s.listCard}>
        <div className={s.auditTools}>{ff !== 'tablet' && search}{filters}{ff === 'tablet' && <div className={s.dchips}>{chips}</div>}</div>
        {ff !== 'tablet' && <div className={s.dchips}>{chips}</div>}
        {status && <p role="status" className={s.result}>{status}</p>}
        {body ? <div className={s.dbody}>{body}</div> : <DataTable cols={cols} rows={list.items} rowKey={(l) => l.id} />}
        {!body && <Pager page={list.page} pageSize={list.pageSize} count={list.items.length} total={list.total} hasNext={list.hasNext} onGoto={list.goto} />}
      </div>
      {cleanupOpen && <AuditCleanupDialog retainedCount={count90.data?.pagination.total ?? list.total} onClose={() => setCleanupOpen(false)} onDeleted={(count) => { setCleanupOpen(false); setStatus(`已清理 ${count} 条 90 天前日志`); list.reload(); }} />}
    </div>
  );
}
